import { Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { DealerService, HostsService, TimezoneService } from '@core';
import { ApiHostLocation } from '@core/services/hosts/hosts.interfaces';
import { Autocomplete, AutocompleteOption, Input } from '@ntv360/component-pantry';
import { catchError, forkJoin, of } from 'rxjs';

@Component({
    selector: 'app-details',
    imports: [Autocomplete, Input, FormsModule],
    templateUrl: './details.component.html',
    styleUrl: './details.component.scss',
})
export class DetailsComponent {
    private dealerService = inject(DealerService);
    private timezoneService = inject(TimezoneService);
    private destroyRef = inject(DestroyRef);

    public hostData = input<ApiHostLocation | null>(null);

    // Autocomplete options loaded from API
    public dealerOptions = signal<AutocompleteOption[]>([]);
    public timezoneOptions = signal<AutocompleteOption[]>([]);

    public cityOptions: AutocompleteOption[] = [];

    // Form field signals
    public selectedDealer = signal<string | null>(null);
    public hostBusinessName = signal('');
    public contactNumber = signal('');
    public latitude = signal('');
    public longitude = signal('');
    public address = signal('');
    public selectedCity = signal<string | null>(null);
    public state = signal('');
    public region = signal('');
    public zipCode = signal('');
    public selectedTimezone = signal<number | null>(null);
    public notes = signal('');

    constructor() {
        // Load dealers and timezones in parallel
        forkJoin({
            dealers: this.dealerService
                .getAll({ fields: ['clientInformationId', 'businessName', 'ownerId'], limit: 9999 })
                .pipe(catchError(() => of({ data: [] as any[] }))),
            timezones: this.timezoneService
                .getAll()
                .pipe(catchError(() => of({ data: [] as any[] }))),
        })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ dealers, timezones }) => {
                console.log('dealers response:', dealers);
                this.dealerOptions.set(
                    dealers.data.map((d) => ({ value: d.ownerId, label: d.businessName })),
                );
                this.timezoneOptions.set(
                    timezones.data.map((t: any) => ({
                        value: t.timezoneId,
                        label: t.timezoneName,
                    })),
                );
            });

        // Pre-fill form whenever hostData or options change
        effect(() => {
            const host = this.hostData();
            // Track options so effect re-runs when they load
            this.dealerOptions();
            this.timezoneOptions();

            if (!host) return;

            this.hostBusinessName.set(host.hostLocationName ?? '');
            this.contactNumber.set(host.contactNumber ?? '');
            this.latitude.set(host.latitude ?? '');
            this.longitude.set(host.longitude ?? '');
            this.address.set(host.addressLine1 ?? '');
            this.selectedCity.set(host.city ?? null);
            this.state.set(host.state ?? '');
            this.region.set(host.region ?? '');
            this.zipCode.set(host.zip ?? '');
            this.notes.set(host.notes ?? '');
            this.selectedDealer.set(host.ownerId ?? null);
            this.selectedTimezone.set(host.timezoneId ?? null);
        });
    }
}
